import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useState } from 'react';

/**
 * Utility hook to manage query parameters in the URL and handle browser back button navigation.
 */
const useUpdateCheckoutUrlParams = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('step') ?? 'home';
  });

  /**
   * Updates the URL query parameters based on the given key-value pairs.
   * If the value of a key is `null` or `undefined`, the corresponding parameter will be removed.
   * @param {Record<string, string | null | undefined>} params - Key-value pairs to update in the URL.
   * @param {boolean} replace - Whether to replace the current history entry or push a new one.
   */
  const updateCheckoutUrlParams = useCallback(
    (params: Record<string, string | null | undefined>, replace = false) => {
      const searchParams = new URLSearchParams(location.search);

      Object.entries(params).forEach(([key, value]) => {
        if (!value) {
          searchParams.delete(key);
        } else {
          searchParams.set(key, value);
        }
      });

      const updatedParams = searchParams.toString();
      const newUrl = updatedParams ? `${location.pathname}?${updatedParams}` : location.pathname;

      navigate(newUrl, { replace });

      const step = searchParams.get('step') ?? 'home';
      setCurrentStep(step);
    },
    [navigate, location]
  );

  return { updateCheckoutUrlParams, currentStep };
};

export default useUpdateCheckoutUrlParams;
